// src/pages/admin/PhysicalBill.jsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FiPlus, FiTrash2, FiFileText,
  FiUser, FiPhone, FiMapPin, FiHash, FiCalendar, FiPackage, FiEye, FiClipboard
} from "react-icons/fi";
import logo from "../../assests/logo.png";
import Modal from "../../components/common/Modal";
import Spinner from "../../components/common/Spinner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getProducts, getPhysicalOrders, addPhysicalOrder, deletePhysicalOrder,
} from "../../services/firestoreService";

// Default SGST/CGST rates (%) and delivery charge (₹ per kg) — all editable by the admin per bill.
const DEFAULT_SGST_PERCENT = 2.5;
const DEFAULT_CGST_PERCENT = 2.5;
const DEFAULT_DELIVERY_PER_KG = 0.5;

// ─── Company details (same as Admin Orders invoice) ─────────────────────────
const COMPANY = {
  name: "TRIPURA AGRO INDUSTRY(OPC) PVT LTD",
  address: "DIGHAPHANDI,DIST-GANJAM,ODISHA-761012",
  gstin: "GSTIN/UNI : 21AAJCT1379F1ZG",
  phone: "PH-9778553533",
  stateLine: "State Name : Odisha, Code : 21",
  bankName: "IDFC First Bank Gold A/c No.59778553533",
  bankAccNo: "59778553533",
  bankIfsc: "Branch & IFS Code : Berhampur & IDFB0060743",
  logoDataUrl: logo,
};

// ─── Number to words (for "Amount Chargeable in words") ───────────────────────
function numberToWords(num) {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
    if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + inWords(n % 1000) : "");
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + inWords(n % 100000) : "");
    return inWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + inWords(n % 10000000) : "");
  }

  const rupees = Math.floor(num);
  const words = rupees === 0 ? "Zero" : inWords(rupees);
  return `INR ${words} Only`;
}

// ─── Invoice Generator (same Tax Invoice design as Admin Orders) ──────────────
function generatePhysicalInvoice(order) {
  const doc = new jsPDF({ compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 14;
  const right = pageWidth - 14;

  const invoiceNo = order.billNumber || String(order.id || "").slice(0, 8).toUpperCase();
  const orderDate = order.billDate
    ? new Date(order.billDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
    : "—";

  let y = 14;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(17, 24, 39);
  doc.text("INVOICE", pageWidth / 2, y, { align: "center" });
  y += 6;

  const boxTop = y;
  doc.setDrawColor(0, 0, 0);

  // ── Logo (top-left, before company name) ──
  const logoSize = 18;
  const logoX = left + 2;
  const textX = left + 2 + logoSize + 3;

  if (COMPANY.logoDataUrl) {
    try {
      doc.addImage(COMPANY.logoDataUrl, "PNG", logoX, y + 2, logoSize, logoSize);
    } catch (e) {
      doc.rect(logoX, y + 2, logoSize, logoSize);
    }
  } else {
    doc.setDrawColor(180, 180, 180);
    doc.rect(logoX, y + 2, logoSize, logoSize);
    doc.setFontSize(6);
    doc.setTextColor(160, 160, 160);
    doc.text("LOGO", logoX + logoSize / 2, y + 2 + logoSize / 2 + 1, { align: "center" });
    doc.setDrawColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);
  }

  // ── Seller (top-left, next to logo) ──
  const sellerMaxWidth = (pageWidth / 2 + 2) - textX - 2;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  const nameLines = doc.splitTextToSize(COMPANY.name, sellerMaxWidth);
  doc.text(nameLines, textX, y + 5);
  const nameBlockHeight = nameLines.length * 4.2;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  let sellerY = y + 5 + nameBlockHeight + 2.5;
  doc.text(doc.splitTextToSize(COMPANY.address, sellerMaxWidth), textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.gstin, textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.phone, textX, sellerY);
  sellerY += 4.5;
  doc.text(COMPANY.stateLine, textX, sellerY);

  // ── Invoice No. & Date (top-right) ──
  const metaX = pageWidth / 2 + 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Invoice No.", metaX, y + 6);
  doc.text("Dated", metaX + 55, y + 6);
  doc.setFont("helvetica", "bold");
  doc.text(invoiceNo, metaX, y + 12);
  doc.text(orderDate, metaX + 55, y + 12);

  const headerRowHeight = Math.max(31, sellerY - y + 6);
  y += headerRowHeight;
  doc.line(left, y, right, y);

  // ── Buyer (Bill to) ──
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Buyer (Bill to)", left + 2, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(order.customerName || "—", left + 2, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  let buyerLineY = y + 18;
  if (order.address) {
    const lines = doc.splitTextToSize(order.address, (pageWidth - 28) * 0.6);
    doc.text(lines, left + 2, buyerLineY);
    buyerLineY += lines.length * 5;
  }
  doc.text(`Phone: ${order.mobile || "—"}`, left + 2, buyerLineY);
  buyerLineY += 5;
  if (order.gstNumber) {
    doc.text(`GSTIN: ${order.gstNumber}`, left + 2, buyerLineY);
    buyerLineY += 5;
  }

  // ── Right column: Delivery Note / Mode of Payment / Bill of Lading / Vehicle No ──
  const payX = pageWidth / 2 + 6;
  const colDividerX = pageWidth / 2 + 2 + (right - (pageWidth / 2 + 2)) / 2;
  const pay2X = colDividerX + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  doc.text("Delivery Note", payX, y + 6);
  doc.text("Mode/Terms of Payment", pay2X, y + 6);
  doc.setFont("helvetica", "bold");
  doc.text("Cash / Counter Sale", pay2X, y + 12);

  const rowDividerY = y + 16;
  doc.setFont("helvetica", "normal");
  doc.text("Bill of Lading/LR-RR No.", payX, rowDividerY + 6);
  doc.text("Motor Vehicle No.", pay2X, rowDividerY + 6);

  const rightColBottom = rowDividerY + 16;

  y = Math.max(buyerLineY, rightColBottom) + 4;
  doc.line(left, y, right, y);
  y += 4;

  // Outer box around the whole header
  doc.rect(left, boxTop, right - left, y - boxTop);
  doc.line(pageWidth / 2 + 2, boxTop, pageWidth / 2 + 2, y);
  doc.line(left, boxTop + headerRowHeight, pageWidth / 2 + 2, boxTop + headerRowHeight);
  doc.line(colDividerX, boxTop + headerRowHeight, colDividerX, y);
  doc.line(pageWidth / 2 + 2, rowDividerY, right, rowDividerY);

  y += 2;

  // ── Items table ──
  const items = order.items || [];
  const resolvedItems = items.map((item) => {
    const qty = Number(item.qty) || 0;
    const rate = Number(item.price) || 0;
    const amount = rate * qty;
    return {
      name: item.name,
      weight: item.weight || "",
      qty,
      unit: item.unit || "",
      rate,
      amount,
    };
  });

  // ── Delivery / SGST / CGST — use saved values, falling back for older bills saved before these existed ──
  const subtotalVal = Number(order.subtotal || 0);
  const totalKgVal = order.totalKg != null ? Number(order.totalKg) : 0;
  const deliveryPerKgVal = order.deliveryPerKg != null ? Number(order.deliveryPerKg) : DEFAULT_DELIVERY_PER_KG;
  const sgstPercentVal = order.sgstPercent != null ? Number(order.sgstPercent) : DEFAULT_SGST_PERCENT;
  const cgstPercentVal = order.cgstPercent != null ? Number(order.cgstPercent) : DEFAULT_CGST_PERCENT;
  const deliveryChargeVal = order.deliveryCharge != null ? Number(order.deliveryCharge) : totalKgVal * deliveryPerKgVal;
  const sgstAmountVal = order.sgstAmount != null ? Number(order.sgstAmount) : subtotalVal * (sgstPercentVal / 100);
  const cgstAmountVal = order.cgstAmount != null ? Number(order.cgstAmount) : subtotalVal * (cgstPercentVal / 100);
  const grandTotalVal = Number(order.grandTotal || (subtotalVal + deliveryChargeVal + sgstAmountVal + cgstAmountVal));

  autoTable(doc, {
    startY: y,
    head: [["Sl\nNo.", "Description of Goods", "Weight/Pack", "Quantity", "Rate", "per", "Amount"]],
    body: resolvedItems.map((it, i) => [
      i + 1,
      it.name,
      it.weight || "—",
      it.qty,
      it.rate.toFixed(2),
      it.unit,
      it.amount.toFixed(2),
    ]),
    foot: [
      [{ content: "Subtotal", colSpan: 6, styles: { halign: "right", fontStyle: "normal" } }, subtotalVal.toFixed(2)],
      [{ content: `Delivery Charge (${totalKgVal.toFixed(2)} kg x Rs.${deliveryPerKgVal}/kg)`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } }, deliveryChargeVal.toFixed(2)],
      [{ content: `SGST (${sgstPercentVal}%)`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } }, sgstAmountVal.toFixed(2)],
      [{ content: `CGST (${cgstPercentVal}%)`, colSpan: 6, styles: { halign: "right", fontStyle: "normal" } }, cgstAmountVal.toFixed(2)],
      [{ content: "Grand Total", colSpan: 6, styles: { halign: "right", fontStyle: "bold" } }, `Rs.${grandTotalVal.toFixed(2)}`],
    ],
    theme: "grid",
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8.5, fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.2 },
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 9, fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.2 },
    bodyStyles: { fontSize: 8.5, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 22, halign: "center" },
      3: { cellWidth: 20, halign: "right" },
      4: { cellWidth: 18, halign: "right" },
      5: { cellWidth: 14, halign: "center" },
      6: { cellWidth: 26, halign: "right" },
    },
    margin: { left, right: 14 },
    minCellHeight: 8,
  });

  let finalY = doc.lastAutoTable.finalY;

  // ── Amount in words + bank details block ──
  const blockTop = finalY;
  doc.rect(left, blockTop, right - left, 34);
  doc.line(pageWidth / 2 + 2, blockTop, pageWidth / 2 + 2, blockTop + 34);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.text("Amount Chargeable (in words)", left + 2, blockTop + 6);
  doc.setFont("helvetica", "bold");
  const wordsLines = doc.splitTextToSize(numberToWords(grandTotalVal), (pageWidth - 28) * 0.45);
  doc.text(wordsLines, left + 2, blockTop + 12);
  doc.setFont("helvetica", "normal");
  doc.text("E. & O.E", right - 2, blockTop + 6, { align: "right" });

  const bankX = pageWidth / 2 + 6;
  doc.text("Company's Bank Details", bankX, blockTop + 6);
  doc.text(`Bank Name : ${COMPANY.bankName}`, bankX, blockTop + 12);
  doc.text(`A/c No.   : ${COMPANY.bankAccNo}`, bankX, blockTop + 18);
  doc.text(COMPANY.bankIfsc, bankX, blockTop + 24);

  finalY = blockTop + 34;

  // ── Declaration + Signatory ──
  const declTop = finalY;
  doc.rect(left, declTop, right - left, 22);
  doc.line(pageWidth / 2 + 2, declTop, pageWidth / 2 + 2, declTop + 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Declaration", left + 2, declTop + 6);
  const declText = "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.";
  const declLines = doc.splitTextToSize(declText, (pageWidth - 28) * 0.45);
  doc.text(declLines, left + 2, declTop + 11);

  doc.setFont("helvetica", "bold");
  const forCompanyLines = doc.splitTextToSize(`for ${COMPANY.name}`, right - (pageWidth / 2 + 6) - 2);
  doc.text(forCompanyLines, right - 2, declTop + 6, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.text("Authorised Signatory", right - 2, declTop + 19, { align: "right" });

  finalY = declTop + 22 + 8;

  // Footer note
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text("This is a Computer Generated Invoice", pageWidth / 2, finalY, { align: "center" });

  doc.save(`Invoice_${invoiceNo}.pdf`);
}

export default function PhysicalBill() {
  const [items, setItems] = useState([{ name: "", qty: 1, unit: "kg", price: "", weight: "", rateInfo: "", isCustom: false }]);
  const [products, setProducts] = useState([]);
  const [savedOrders, setSavedOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null); // physical order shown in the View modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [invoiceGeneratingId, setInvoiceGeneratingId] = useState(null); // order id currently generating a PDF

  // Editable per-bill rates — pre-filled with sensible defaults, admin can change per bill.
  const [deliveryPerKg, setDeliveryPerKg] = useState(DEFAULT_DELIVERY_PER_KG);
  const [sgstPercent,   setSgstPercent]   = useState(DEFAULT_SGST_PERCENT);
  const [cgstPercent,   setCgstPercent]   = useState(DEFAULT_CGST_PERCENT);

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch((err) => console.error("Failed to load products:", err));
    refreshOrders();
  }, []);

  const refreshOrders = () => {
    setLoadingOrders(true);
    getPhysicalOrders()
      .then(setSavedOrders)
      .catch((err) => console.error("Failed to load physical orders:", err))
      .finally(() => setLoadingOrders(false));
  };

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      customerName: "",
      mobile: "",
      address: "",
      gstNumber: "",
      billDate: new Date().toISOString().split("T")[0],
      billNumber: `TA-${Date.now().toString().slice(-6)}`,
    },
  });

  const addItem = () => setItems([...items, { name: "", qty: 1, unit: "kg", price: "", weight: "", rateInfo: "", isCustom: false }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i][field] = value;
    setItems(updated);
  };

  // Reads the first number out of a weight string like "30 kg" or "20 pack(s)".
  const parseWeightNum = (weightSize) => {
    const match = String(weightSize || "").match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  };

  // Total kg this line item represents (used for the per-kg delivery charge).
  // Example: item.weight = "30 kg" (the pack size) and qty = 2 packs → 60 kg.
  // The Weight/Pack field is sometimes just a plain number (e.g. "30" with no
  // "kg" text) — in that case its unit is whatever the item's own Unit column
  // says (kg/g). If there's no weight/pack at all (custom item sold directly
  // by kg), qty itself is treated as kg. Anything not weight-based
  // (pcs, box, dozen…) contributes 0 kg.
  const parseItemKg = (item) => {
    const qty = parseFloat(item.qty) || 0;
    if (!qty) return 0;

    const weightStr = String(item.weight || "").trim();
    const itemUnit = (item.unit || "").toLowerCase();

    if (weightStr) {
      // Explicit unit written into the weight text itself, e.g. "30 kg" / "500 g"
      const explicit = weightStr.match(/([\d.]+)\s*(kg|g)\b/i);
      if (explicit) {
        const num = parseFloat(explicit[1]);
        const unit = explicit[2].toLowerCase();
        return (unit === "g" ? num / 1000 : num) * qty;
      }
      // Plain number with no unit text — interpret it using the item's own unit
      const num = parseWeightNum(weightStr);
      if (num != null) {
        if (itemUnit === "g") return (num / 1000) * qty;
        if (itemUnit === "kg") return num * qty;
        return 0; // weight given but not a kg/g unit (e.g. pcs, box) — not weight-based
      }
    }

    // No weight/pack at all — sold directly by kg/g, so qty itself is the amount
    if (itemUnit === "kg") return qty;
    if (itemUnit === "g") return qty / 1000;
    return 0;
  };

  // Builds the informational "₹107/kg" style rate shown in the product table —
  // uses the product's own rate field if set, otherwise derives it from
  // price ÷ weight (the same math the product table uses).
  const formatRateInfo = (product) => {
    if (!product) return "";
    if (product.rate != null && product.rate !== "") return `₹${product.rate}/${product.unit || ""}`;
    const weightNum = parseWeightNum(product.weightSize);
    if (product.price != null && weightNum) return `₹${(product.price / weightNum).toFixed(0)}/${product.unit || ""}`;
    return "";
  };

  // Item-name dropdown: selecting a catalog product fills name, weight/pack,
  // unit, the per-pack price (used for totals) and an informational rate
  // (e.g. "₹107/kg", matching the product table) straight from catalog data.
  // These fields are then locked for catalog items — only quantity stays
  // editable, and changing qty never touches them, it just recalculates the
  // line's total Price.
  // Selecting "Custom item" switches the row to free-text inputs for items
  // not in the catalog.
  const handleProductSelect = (i, value) => {
    const updated = [...items];
    if (value === "__custom__") {
      updated[i] = { ...updated[i], name: "", weight: "", rateInfo: "", isCustom: true };
      setItems(updated);
      return;
    }
    const product = products.find((p) => p.id === value);
    updated[i] = {
      ...updated[i],
      name: product?.name || "",
      isCustom: false,
      unit: product?.unit || updated[i].unit,
      weight: product?.weightSize || "",
      rateInfo: formatRateInfo(product),
      // Price = the catalog's per-pack/unit price — what totals are based on.
      price: product?.price != null ? product.price : updated[i].price,
    };
    setItems(updated);
  };

  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.qty) || 0), 0);
  const totalKg = items.reduce((sum, item) => sum + parseItemKg(item), 0);
  const deliveryCharge = totalKg * (parseFloat(deliveryPerKg) || 0);
  const sgstAmount = subtotal * ((parseFloat(sgstPercent) || 0) / 100);
  const cgstAmount = subtotal * ((parseFloat(cgstPercent) || 0) / 100);
  const grandTotal = subtotal + deliveryCharge + sgstAmount + cgstAmount;

  // "Apply Order" — saves the bill as a physical order so it shows up in the
  // table below, then clears the form so the next bill can be started fresh.
  // (Use the View/Download buttons on a saved row to view or download the invoice.)
  const onSubmit = async (data) => {
    // Guard: don't save an empty/blank bill (e.g. all rows blank or qty 0)
    const validItems = items.filter((it) => it.name && Number(it.qty) > 0);
    if (validItems.length === 0) {
      alert("Please add at least one item with a name and quantity before saving.");
      return;
    }

    // Normalize every item to plain, well-typed values. This is important
    // because Firestore's addDoc() throws (and the save silently fails) if
    // ANY field — even one nested inside an array — is `undefined`.
    const normalizedItems = validItems.map((it) => ({
      name: it.name || "",
      qty: Number(it.qty) || 0,
      unit: it.unit || "",
      price: Number(it.price) || 0,
      weight: it.weight || "",
      rateInfo: it.rateInfo || "",
      isCustom: !!it.isCustom,
    }));

    const normalizedSubtotal = normalizedItems.reduce((sum, it) => sum + it.price * it.qty, 0);
    const normalizedTotalKg = normalizedItems.reduce((sum, it) => sum + parseItemKg(it), 0);
    const normalizedDeliveryPerKg = parseFloat(deliveryPerKg) || 0;
    const normalizedSgstPercent = parseFloat(sgstPercent) || 0;
    const normalizedCgstPercent = parseFloat(cgstPercent) || 0;
    const normalizedDeliveryCharge = normalizedTotalKg * normalizedDeliveryPerKg;
    const normalizedSgstAmount = normalizedSubtotal * (normalizedSgstPercent / 100);
    const normalizedCgstAmount = normalizedSubtotal * (normalizedCgstPercent / 100);
    const normalizedGrandTotal = normalizedSubtotal + normalizedDeliveryCharge + normalizedSgstAmount + normalizedCgstAmount;

    const order = {
      customerName: data.customerName || "",
      mobile: data.mobile || "",
      address: data.address || "",
      gstNumber: data.gstNumber || "",
      billNumber: data.billNumber || "",
      billDate: data.billDate || new Date().toISOString().split("T")[0],
      items: normalizedItems,
      subtotal: normalizedSubtotal,
      totalKg: normalizedTotalKg,
      deliveryPerKg: normalizedDeliveryPerKg,
      deliveryCharge: normalizedDeliveryCharge,
      sgstPercent: normalizedSgstPercent,
      cgstPercent: normalizedCgstPercent,
      sgstAmount: normalizedSgstAmount,
      cgstAmount: normalizedCgstAmount,
      grandTotal: normalizedGrandTotal,
    };

    setSaving(true);
    try {
      const docRef = await addPhysicalOrder(order);
      setSavedOrders((prev) => [{ id: docRef.id, ...order }, ...prev]);
      setItems([{ name: "", qty: 1, unit: "kg", price: "", weight: "", rateInfo: "", isCustom: false }]);
      reset({
        customerName: "",
        mobile: "",
        address: "",
        gstNumber: "",
        billDate: new Date().toISOString().split("T")[0],
        billNumber: `TA-${Date.now().toString().slice(-6)}`,
      });
    } catch (err) {
      // Surface the real Firestore error (e.g. "Missing or insufficient
      // permissions" means the firestore.rules file needs a rule for the
      // "physicalOrders" collection) instead of a generic message.
      console.error("Failed to save physical order:", err);
      alert(`Could not save this order.\n\n${err?.message || "Please try again."}`);
    } finally {
      setSaving(false);
    }
  };

  // Row actions on the saved-orders table below
  const handleViewOrder = (order) => setSelected(order);
  // Generating the PDF blocks the main thread, so without any visual
  // feedback the click can feel unresponsive until the download appears.
  // Show a spinner first, then defer the heavy work one tick so React
  // gets a chance to paint it before the blocking work starts.
  const handleDownloadOrder = (order) => {
    if (invoiceGeneratingId) return;
    setInvoiceGeneratingId(order.id);
    setTimeout(() => {
      try {
        generatePhysicalInvoice(order);
      } finally {
        setInvoiceGeneratingId(null);
      }
    }, 30);
  };

  const handleDeleteOrder = (order) => setDeleteTarget(order);

  const confirmDeleteOrder = async () => {
    if (!deleteTarget) return;
    try {
      await deletePhysicalOrder(deleteTarget.id);
      setSavedOrders((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      if (selected?.id === deleteTarget.id) setSelected(null);
    } catch (err) {
      console.error("Failed to delete physical order:", err);
      alert("Could not delete this order. Please try again.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const Field = ({ label, name, icon: Icon, rules, type = "text", placeholder, readOnly }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />}
        <input
          {...register(name, rules)}
          type={type}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`w-full rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder-gray-400
            px-3 py-2.5 transition-all outline-none
            ${Icon ? "pl-9" : ""}
            ${readOnly ? "bg-gray-100 cursor-default" : "focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100 hover:border-gray-300"}
            ${errors[name] ? "border-red-300 bg-red-50" : "border-gray-200"}
          `}
        />
      </div>
      {errors[name] && <p className="text-xs text-red-500 mt-1">⚠ {errors[name].message}</p>}
    </div>
  );

  // ── MAIN RENDER ────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Generate Physical Bill</h1>
        <p className="text-sm text-gray-400 mt-0.5">Fill in customer details and items to create a printable invoice</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left: customer + items */}
          <div className="lg:col-span-2 space-y-5">

            {/* Customer details */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
                <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center">
                  <FiUser className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="font-semibold text-gray-900 text-sm">Customer Details</h2>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Customer Name *" name="customerName" icon={FiUser}
                  rules={{ required: "Name is required" }} placeholder="Ramesh Kumar" />
                <Field label="Mobile Number *" name="mobile" icon={FiPhone}
                  rules={{ required: "Mobile is required", pattern: { value: /^[0-9]{10}$/, message: "Enter valid 10-digit number" } }}
                  placeholder="9876543210" />
                <div className="sm:col-span-2">
                  <Field label="Address" name="address" icon={FiMapPin} placeholder="House no, Street, City" />
                </div>
                <Field label="GST Number" name="gstNumber" icon={FiHash} placeholder="22AAAAA0000A1Z5 (optional)" />
              </div>
            </div>

            {/* Bill meta */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
                <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center">
                  <FiFileText className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="font-semibold text-gray-900 text-sm">Bill Details</h2>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Bill Number" name="billNumber" icon={FiHash} readOnly />
                <Field label="Bill Date *" name="billDate" icon={FiCalendar} type="date"
                  rules={{ required: "Date is required" }} />
              </div>
            </div>

            {/* Items */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center">
                    <FiPackage className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="font-semibold text-gray-900 text-sm">Items</h2>
                </div>
                <button type="button" onClick={addItem}
                  className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors">
                  <FiPlus className="w-3.5 h-3.5" /> Add Item
                </button>
              </div>
              <div className="p-5 space-y-3">
                {/* Table header */}
                <div className="hidden sm:grid grid-cols-12 gap-2 px-1">
                  {["Item Name", "Weight/Pack", "Qty", "Unit", "Rate", "Price (₹)", ""].map((h, i) => (
                    <div key={i} className={`text-xs font-semibold text-gray-400 uppercase tracking-wide ${["col-span-3","col-span-2","col-span-1","col-span-1","col-span-2","col-span-2","col-span-1"][i]} ${i > 0 ? "text-center" : ""}`}>{h}</div>
                  ))}
                </div>
                {items.map((item, i) => (
                  <div key={i} className="sm:grid grid-cols-12 gap-2 space-y-2 sm:space-y-0 items-center bg-gray-50 sm:bg-transparent rounded-xl sm:rounded-none p-3 sm:p-0">
                    {item.isCustom ? (
                      <div className="col-span-3 relative">
                        <input value={item.name} onChange={(e) => updateItem(i, "name", e.target.value)}
                          placeholder="Type custom item name"
                          className="w-full rounded-xl border border-gray-200 bg-white text-sm pl-3 pr-16 py-2 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                        <button type="button" onClick={() => handleProductSelect(i, "")}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2 py-1 rounded-lg">
                          Catalog
                        </button>
                      </div>
                    ) : (
                      <select
                        value={products.find((p) => p.name === item.name)?.id || ""}
                        onChange={(e) => handleProductSelect(i, e.target.value)}
                        className="col-span-3 w-full rounded-xl border border-gray-200 bg-white text-sm px-3 py-2 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100">
                        <option value="" disabled>{products.length ? "Select product" : "Loading products..."}</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                        <option value="__custom__">+ Custom item</option>
                      </select>
                    )}
                    {/* Weight/Pack — auto-filled (read-only) from the selected product; typed manually for custom items */}
                    {item.isCustom ? (
                      <input value={item.weight} onChange={(e) => updateItem(i, "weight", e.target.value)}
                        placeholder="e.g. 5 kg" type="text"
                        className="col-span-2 w-full rounded-xl border border-gray-200 bg-white text-sm px-3 py-2 text-center outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                    ) : (
                      <div className="col-span-2 w-full rounded-xl border border-gray-200 bg-gray-100 text-sm px-3 py-2 text-center text-gray-500 truncate">
                        {item.weight || "—"}
                      </div>
                    )}
                    <input value={item.qty} onChange={(e) => updateItem(i, "qty", e.target.value)}
                      type="number" min="1" placeholder="1"
                      className="col-span-1 w-full rounded-xl border border-gray-200 bg-white text-sm px-2 py-2 text-center outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                    {/* Unit — locked to the product's unit for catalog items; editable for custom items */}
                    {item.isCustom ? (
                      <select value={item.unit} onChange={(e) => updateItem(i, "unit", e.target.value)}
                        className="col-span-1 w-full rounded-xl border border-gray-200 bg-white text-sm px-1 py-2 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100">
                        {["kg", "g", "L", "ml", "pcs", "dozen", "bag", "box", "pack"].map(u => <option key={u}>{u}</option>)}
                      </select>
                    ) : (
                      <div className="col-span-1 w-full rounded-xl border border-gray-200 bg-gray-100 text-xs px-1 py-2 text-center text-gray-500 truncate">
                        {item.unit || "—"}
                      </div>
                    )}
                    {/* Rate — informational per-unit rate from the product table (e.g. ₹107/kg); editable price entry for custom items */}
                    {item.isCustom ? (
                      <input value={item.price} onChange={(e) => updateItem(i, "price", e.target.value)}
                        type="number" min="0" placeholder="0.00"
                        className="col-span-2 w-full rounded-xl border border-gray-200 bg-white text-sm px-3 py-2 text-right outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                    ) : (
                      <div className="col-span-2 w-full rounded-xl border border-gray-200 bg-gray-100 text-sm px-2 py-2 text-center text-gray-500 truncate">
                        {item.rateInfo || "—"}
                      </div>
                    )}
                    {/* Price — total for this line (unit price × qty), always read-only, recalculates live as qty changes */}
                    <div className="col-span-2 w-full rounded-xl border border-gray-200 bg-gray-100 text-sm px-3 py-2 text-right font-semibold text-gray-700 truncate">
                      ₹{((parseFloat(item.price) || 0) * (parseInt(item.qty) || 0)).toFixed(2)}
                    </div>
                    <button type="button" onClick={() => removeItem(i)} disabled={items.length === 1}
                      className="col-span-1 flex items-center justify-center w-8 h-8 rounded-lg text-red-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors mx-auto">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm sticky top-24 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h3 className="font-bold text-gray-900 text-sm">Bill Summary</h3>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Items</span>
                  <span className="font-medium text-gray-700">{items.length}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Subtotal</span>
                  <span className="font-medium text-gray-700">₹{subtotal.toFixed(2)}</span>
                </div>

                {/* Editable rate inputs */}
                <div className="pt-1 border-t border-gray-100 space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs text-gray-500">Delivery ₹/kg</label>
                    <input
                      type="number" step="0.1" min="0" value={deliveryPerKg}
                      onChange={(e) => setDeliveryPerKg(e.target.value)}
                      className="w-20 rounded-lg border border-gray-200 bg-gray-50 text-xs text-right px-2 py-1.5 outline-none focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs text-gray-500">SGST %</label>
                    <input
                      type="number" step="0.1" min="0" value={sgstPercent}
                      onChange={(e) => setSgstPercent(e.target.value)}
                      className="w-20 rounded-lg border border-gray-200 bg-gray-50 text-xs text-right px-2 py-1.5 outline-none focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs text-gray-500">CGST %</label>
                    <input
                      type="number" step="0.1" min="0" value={cgstPercent}
                      onChange={(e) => setCgstPercent(e.target.value)}
                      className="w-20 rounded-lg border border-gray-200 bg-gray-50 text-xs text-right px-2 py-1.5 outline-none focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="pt-1 space-y-1.5 border-t border-gray-100">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Delivery Charge ({totalKg.toFixed(2)} kg)</span>
                    <span className="font-medium text-gray-700">₹{deliveryCharge.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>SGST ({sgstPercent}%)</span>
                    <span className="font-medium text-gray-700">₹{sgstAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>CGST ({cgstPercent}%)</span>
                    <span className="font-medium text-gray-700">₹{cgstAmount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-3 flex justify-between">
                  <span className="font-bold text-gray-900 text-sm">Grand Total</span>
                  <span className="font-bold text-xl text-primary-600">₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>
              <div className="px-5 pb-5">
                <button type="submit" disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
                  <FiClipboard className="w-4 h-4" /> {saving ? "Saving..." : "Apply Order"}
                </button>
                <p className="text-center text-xs text-gray-400 mt-2">Saves the bill — view or print it from the table below</p>
              </div>
            </div>
          </div>

        </div>
      </form>

      {/* ── PHYSICAL ORDERS TABLE ──────────────────────────────────────── */}
      <div className="mt-8 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center">
              <FiClipboard className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="font-semibold text-gray-900 text-sm">Physical Orders</h2>
          </div>
          <span className="text-xs font-medium text-gray-400">{savedOrders.length} total</span>
        </div>

        {loadingOrders ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading saved orders...</div>
        ) : savedOrders.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">
            No physical orders yet. Fill the form above and click <span className="font-medium text-gray-500">Apply Order</span>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3">Bill No</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Items</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {savedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3 font-semibold text-gray-900">#{order.billNumber}</td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{order.customerName}</div>
                      <div className="text-xs text-gray-400">{order.mobile}</div>
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {order.items?.length || 0} item{order.items?.length === 1 ? "" : "s"}
                    </td>
                    <td className="px-5 py-3 font-semibold text-primary-700">
                      ₹{Number(order.grandTotal || 0).toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {order.billDate
                        ? new Date(order.billDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                        : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button type="button" title="View" onClick={() => handleViewOrder(order)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:text-gray-400 hover:bg-gray-50 transition-colors">
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button type="button" title="Download Invoice" onClick={() => handleDownloadOrder(order)}
                          disabled={invoiceGeneratingId === order.id}
                          className="flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:text-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-60">
                          {invoiceGeneratingId === order.id ? <Spinner size="sm" /> : <FiFileText className="w-4 h-4" />}
                        </button>
                        <button type="button" title="Delete" onClick={() => handleDeleteOrder(order)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg text-red-500 hover:text-gray-400 hover:bg-gray-50 transition-colors">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Order detail modal (same style as Admin Orders) ── */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title={`Bill #${selected?.billNumber || ""}`} size="lg">
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">Customer:</span> <span className="font-medium ml-1">{selected.customerName}</span></div>
              <div><span className="text-gray-500">Phone:</span> <span className="font-medium ml-1">{selected.mobile}</span></div>
              <div><span className="text-gray-500">GSTIN:</span> <span className="font-medium ml-1">{selected.gstNumber || "—"}</span></div>
              <div><span className="text-gray-500">Payment:</span> <span className="font-medium ml-1">Cash / Counter Sale</span></div>
              <div><span className="text-gray-500">Bill Date:</span>{" "}
                <span className="font-medium ml-1">
                  {selected.billDate
                    ? new Date(selected.billDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </span>
              </div>
              {selected.address && (
                <div className="col-span-2">
                  <span className="text-gray-500">Address:</span>
                  <span className="font-medium ml-1">{selected.address}</span>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Items</h4>
              <div className="space-y-2">
                {selected.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      🌱
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.qty} × ₹{item.price}</p>
                      {(item.weight || item.rateInfo) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {item.weight && (
                            <span className="inline-block text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-1.5 py-0.5">
                              📦 {item.weight}
                            </span>
                          )}
                          {item.rateInfo && (
                            <span className="inline-block text-xs text-primary-600 bg-primary-50 border border-primary-100 rounded-md px-1.5 py-0.5">
                              {item.rateInfo}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="font-semibold">₹{(Number(item.price) * Number(item.qty)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span><span>₹{Number(selected.subtotal || 0).toFixed(2)}</span>
                </div>
                {(() => {
                  const subtotalVal = Number(selected.subtotal || 0);
                  const totalKgVal = selected.totalKg != null ? Number(selected.totalKg) : 0;
                  const deliveryPerKgVal = selected.deliveryPerKg != null ? Number(selected.deliveryPerKg) : 0.5;
                  const sgstPercentVal = selected.sgstPercent != null ? Number(selected.sgstPercent) : 2.5;
                  const cgstPercentVal = selected.cgstPercent != null ? Number(selected.cgstPercent) : 2.5;
                  const deliveryChargeVal = selected.deliveryCharge != null ? Number(selected.deliveryCharge) : totalKgVal * deliveryPerKgVal;
                  const sgstAmountVal = selected.sgstAmount != null ? Number(selected.sgstAmount) : subtotalVal * (sgstPercentVal / 100);
                  const cgstAmountVal = selected.cgstAmount != null ? Number(selected.cgstAmount) : subtotalVal * (cgstPercentVal / 100);
                  return (
                    <>
                      <div className="flex justify-between text-gray-500">
                        <span>Delivery Charge ({totalKgVal.toFixed(2)} kg)</span><span>₹{deliveryChargeVal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>SGST ({sgstPercentVal}%)</span><span>₹{sgstAmountVal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>CGST ({cgstPercentVal}%)</span><span>₹{cgstAmountVal.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
                <div className="flex justify-between font-bold text-base pt-1">
                  <span>Total</span><span className="text-primary-600">₹{Number(selected.grandTotal || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Invoice button inside modal too */}
            <div className="pt-1">
              <button
                onClick={() => handleDownloadOrder(selected)}
                disabled={invoiceGeneratingId === selected.id}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
                {invoiceGeneratingId === selected.id ? (
                  <><Spinner size="sm" /> Generating...</>
                ) : (
                  <><FiFileText className="w-4 h-4" /> Download Invoice</>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete confirmation modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Bill" size="sm">
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete bill{" "}
              <span className="font-mono font-semibold text-gray-900">#{deleteTarget.billNumber}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={confirmDeleteOrder}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}